import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import type { PropType } from 'vue';
import { toRefs, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useQuasar } from 'quasar';

// Stores
import { useRouterStore } from '@stores/router';

// Personal libs
import { ILeftMenuItem } from '@shared/interface/menu.interface';
import { RouteEnum } from '@shared/enum/route.enum';
import { $t } from '@i18n/utils';

/**
 * Globals
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'QMenuItem',
  props: {
  menuItem: { required: true, type: Object as PropType<ILeftMenuItem> },
  nbUsersToValidate: { required: false, type: Number },
  nbVideosToValidate: { required: false, type: Number },
},
  setup(__props) {

// Vendor
const $q = useQuasar()

/**
 * Stores
 */
const routerStore = useRouterStore();

/**
 * Globals
 */
const router = useRouter();

/**
 *  Properties
 */
const props = __props;
const { menuItem, nbUsersToValidate, nbVideosToValidate } = toRefs(props);

/**
 * Reactive datas
 */
const displayUserBadge = computed<boolean>(() => {
  if (menuItem.value.pageLink == RouteEnum.USERS) {
    return true;
  } else {
    return false;
  }
});

const displayVideoBadge = computed<boolean>(() => {
  if (menuItem.value.pageLink == RouteEnum.VIDEOS) {
    return true;
  } else {
    return false;
  }
});

/**
 * Computed properties
 */
const activeRoute = computed<RouteEnum | undefined>({
  get: (): RouteEnum | undefined => routerStore.activeRoute,
  set: (value: RouteEnum | undefined) => routerStore.setActiveRoute(value),
});
const title = computed<string>(() => {
  let activeRouteTxt = '';
  let caption = '';

  if (activeRoute.value === menuItem.value.pageLink) {
    activeRouteTxt = ' (' + $t('layoutLeftSide.menu.activeLink') + ')';
  }
  if (menuItem.value.caption) {
    caption = ` (${menuItem.value.caption})`;
  }

  const userSuffix =
    displayUserBadge.value &&
    nbUsersToValidate?.value != null &&
    nbUsersToValidate.value > 0
      ? ` (${nbUsersToValidate.value} utilisateur${
          nbUsersToValidate.value > 1 ? 's' : ''
        } à valider)`
      : null;
  const videoSuffix =
    displayVideoBadge.value &&
    nbVideosToValidate?.value != null &&
    nbVideosToValidate.value > 0
      ? ` (${nbVideosToValidate.value} vidéo${
          nbVideosToValidate.value > 1 ? 's' : ''
        } à valider)`
      : null;

  return `Aller vers : ${menuItem.value.label}${caption}${activeRouteTxt}${
    userSuffix ?? ''
  }${videoSuffix ?? ''}`;
});

/**
 * Methods
 */
function goTo() {
  // First, clean navigation spool
  routerStore.clearOpenedModal();

  // Then go to
  router.push(`/${menuItem.value.pageLink}`);
}

return (_ctx: any,_cache: any) => {
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_item_section = _resolveComponent("q-item-section")!
  const _component_q_badge = _resolveComponent("q-badge")!
  const _component_q_item_label = _resolveComponent("q-item-label")!
  const _component_q_item = _resolveComponent("q-item")!
  const _directive_ripple = _resolveDirective("ripple")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_unref(menuItem).topSeparator)
      ? (_openBlock(), _createBlock(_component_q_separator, {
          key: 0,
          class: _normalizeClass([
      'separator',
      { 'bg-white': _unref($q).dark.isActive, 'bg-black': !_unref($q).dark.isActive },
    ]),
          "aria-hidden": "true"
        }, null, 8 /* PROPS */, ["class"]))
      : _createCommentVNode("v-if", true),
    _createCommentVNode(" :to=\"`/${menuItem.pageLink}`\" "),
    _withDirectives((_openBlock(), _createBlock(_component_q_item, {
      role: "listitem",
      clickable: "",
      onClick: _cache[0] || (_cache[0] = ($event: any) => (goTo())),
      active: activeRoute.value === _unref(menuItem).pageLink,
      class: "set-outline set-outline-offset q-py-none q-my-none",
      "active-class": `
      bg-primary text-white
      ${
        _unref($q).dark.isActive
          ? 'active-item-border-dark'
          : 'active-item-border-light'
      }
    `,
      title: title.value
    }, {
      default: _withCtx(() => [
        _createVNode(_component_q_item_section, { avatar: "" }, {
          default: _withCtx(() => [
            _createVNode(_component_q_icon, {
              name: _unref(menuItem).icon,
              class: "q-icon-active-class-light"
            }, null, 8 /* PROPS */, ["name"])
          ]),
          _: 1 /* STABLE */
        }),
        _createVNode(_component_q_item_section, null, {
          default: _withCtx(() => [
            _createVNode(_component_q_item_label, { class: "text-uppercase" }, {
              default: _withCtx(() => [
                _createElementVNode("div", null, [
                  _createTextVNode(_toDisplayString(_unref(menuItem).label) + " ", 1 /* TEXT */),
                  _createCommentVNode(" User badge "),
                  (
              displayUserBadge.value &&
              _unref(nbUsersToValidate) != null &&
              _unref(nbUsersToValidate) > 0
            )
                    ? (_openBlock(), _createBlock(_component_q_badge, {
                        key: 0,
                        color: "orange",
                        "text-color": "black",
                        rounded: "",
                        align: "top",
                        floating: ""
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(nbUsersToValidate) > 9 ? '9+' : _unref(nbUsersToValidate)), 1 /* TEXT */)
                        ]),
                        _: 1 /* STABLE */
                      }))
                    : _createCommentVNode("v-if", true),
                  _createCommentVNode(" Video badge "),
                  (
              displayVideoBadge.value &&
              _unref(nbVideosToValidate) != null &&
              _unref(nbVideosToValidate) > 0
            )
                    ? (_openBlock(), _createBlock(_component_q_badge, {
                        key: 1,
                        color: "orange",
                        "text-color": "black",
                        rounded: "",
                        align: "top",
                        floating: ""
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(nbVideosToValidate) > 9 ? '9+' : _unref(nbVideosToValidate)), 1 /* TEXT */)
                        ]),
                        _: 1 /* STABLE */
                      }))
                    : _createCommentVNode("v-if", true)
                ])
              ]),
              _: 1 /* STABLE */
            }),
            (_unref(menuItem).caption)
              ? (_openBlock(), _createBlock(_component_q_item_label, {
                  key: 0,
                  class: "text-caption"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(menuItem).caption), 1 /* TEXT */)
                  ]),
                  _: 1 /* STABLE */
                }))
              : _createCommentVNode("v-if", true)
          ]),
          _: 1 /* STABLE */
        })
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["active", "active-class", "title"])), [
      [_directive_ripple]
    ]),
    (_unref(menuItem).bottomSeparator)
      ? (_openBlock(), _createBlock(_component_q_separator, {
          key: 1,
          class: _normalizeClass([
      'separator',
      { 'bg-white': _unref($q).dark.isActive, 'bg-black': !_unref($q).dark.isActive },
    ])
        }, null, 8 /* PROPS */, ["class"]))
      : _createCommentVNode("v-if", true)
  ], 64 /* STABLE_FRAGMENT */))
}
}

})